import React, { useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { Random } from '@zedoc/random';
import RecipientFilterOption from '../../../../common/models/RecipientFilterOption';
import {
  apiZedocProjectVariablesSummary,
  apiZedocSearchRecipientsFilterOptions,
} from '../../../../common/api/zedoc';
import {
  FILTER_CONDITION__SEARCH_TERMS,
  FILTER_CONDITION__TEXT,
  FILTER_TYPE__ALL_OF,
} from '../../../../common/constants';
import createSelectors from '../../../../common/selectors/createSelectors';
import { default as VariableSelect } from '../../../../common/selectors/Variable';
import defaultFilters from './defaultFilters';
import { FILTER_CATEGORY__VARIABLE } from '../../../../components/Filters';
import usePatientFilters from '../../../../utils/usePatientFilters';
import FiltersToolbar from './FiltersToolbar';

const RecipientFilterOptionSelect = createSelectors(RecipientFilterOption, {});

const ConnectedFilters = () => {
  const variablesDb = useSelector(
    VariableSelect.all()
      // eslint-disable-next-line no-underscore-dangle
      .satisfying((variable) => !isEmpty(variable._projects))
      .byId(),
  );
  useDDPSubscription(apiZedocProjectVariablesSummary.withParams({}));
  const presets = usePatientFilters({
    defaultFilters: useMemo(() => {
      return defaultFilters.filter((preset) => {
        if (!preset?.meta?.includeIfVariableIdPresent) {
          return true;
        }
        return !!variablesDb[preset.meta.includeIfVariableIdPresent];
      });
    }, [variablesDb]),
    defaultCategory: FILTER_CATEGORY__VARIABLE,
  });
  const getOptionsSubscription = useCallback(
    ({
      searchText,
      type,
      condition,
      state,
      settings,
      pageIndex,
      resultsPerPage,
    }) => {
      return apiZedocSearchRecipientsFilterOptions.withParams({
        controlId: '$meta.id',
        searchText,
        filter: {
          type,
          condition,
          state,
          settings,
        },
        pageIndex,
        resultsPerPage,
        sorterFilter: {
          label: 1,
        },
      });
    },
    [],
  );

  const simpleFilters = useMemo(
    () => [
      {
        id: Random.id(),
        type: FILTER_TYPE__ALL_OF,
        settings: {
          filters: [
            // Full Name.
            //
            // SEARCH_TERMS against the tokenised `nameTerms` array, NOT
            // FULL_TEXT against `name.text`. FULL_TEXT compiles to an unanchored
            // `/text/i`, which gives MongoDB no bounds to seek on, so every
            // keystroke read all 622k recipients - measured at ~5.7s for a
            // single-patient lookup against ~164ms for an anchored one.
            //
            // SEARCH_TERMS anchors per word (`^word`), which an index can serve.
            // Matching against one array entry per name word means any WORD of
            // the name can be searched from its start: a surname alone still
            // finds the patient. Switching to TEXT would have been faster to
            // write but would only match from the start of the full name.
            //
            // The trade-off, accepted for the timing above: this is a
            // word-prefix match, not the substring match FULL_TEXT gave. Text
            // from the middle of a word no longer matches - "ann" does not find
            // "Joanne", and "Jones" does not find "Smith-Jones" (one word, no
            // whitespace).
            {
              ...defaultFilters[0],
              id: Random.id(),
              // review:accepted: components/MatchedProperties.js skips SEARCH_TERMS filters, so the "Full name: ..." matched line no longer appears under a row for this box. Not a defect: the full name is already shown in the same cell (PatientLink), so the line only repeated it.
              condition: FILTER_CONDITION__SEARCH_TERMS,
              settings: { id: 'nameTerms' },
              state: { text: '' },
              meta: {
                ...defaultFilters[0].meta,
                // The preset's list is for `name.text` and lacks SEARCH_TERMS;
                // without it the condition picker cannot show this entry's
                // condition. The others make no sense on `nameTerms`.
                conditions: [FILTER_CONDITION__SEARCH_TERMS],
              },
            },
          ],
        },
        meta: {
          nested: true,
        },
      },
      {
        id: Random.id(),
        type: FILTER_TYPE__ALL_OF,
        settings: {
          filters: [
            // First Name.
            //
            // TEXT, not FULL_TEXT: `name.given.0` holds a single word, so an
            // anchored prefix is what a first-name search means anyway, and it
            // is the condition the filter panel already uses for this field.
            {
              ...defaultFilters[1],
              id: Random.id(),
              condition: FILTER_CONDITION__TEXT,
              state: { text: '' },
            },
          ],
        },
        meta: {
          nested: true,
        },
      },
      {
        id: Random.id(),
        type: FILTER_TYPE__ALL_OF,
        settings: {
          filters: [
            // Last Name - same reasoning as First Name, on `name.family`.
            {
              ...defaultFilters[2],
              id: Random.id(),
              condition: FILTER_CONDITION__TEXT,
              state: { text: '' },
            },
          ],
        },
        meta: {
          nested: true,
        },
      },
    ],
    [],
  );

  return (
    <FiltersToolbar
      presets={presets}
      simpleFilters={simpleFilters}
      optionsSelector={RecipientFilterOptionSelect}
      optionsSubscription={getOptionsSubscription}
    />
  );
};

ConnectedFilters.propTypes = {};

export default ConnectedFilters;
